import { captureException } from "@sentry/react";
import * as client from "_graphql-types";
import { filterSortPage } from "../DataProvider";
import * as types from "./types";

type Sdk = ReturnType<typeof client.getSdk>;

/*********************************************************************/
// Data Provider Functions
/*********************************************************************/

async function getList(
  sdk: Sdk,
  params: types.GetListParams<client.GroupShareDefinitionSortEnum>
) {
  const {
    pagination: { page, perPage },
    sort: { field, order },
    filter,
  } = params;

  const {
    groupShareDefinitionList: { items: data, total },
  } = await sdk.getListGroupShareDefinition({
    page: {
      offset: (page - 1) * perPage,
      limit: perPage,
    },
    sort: [{ field, order }],
    filter,
  });

  return {
    data,
    total,
  };
}

// Flatten the entity-namespaced rule output into the form's flat fields
// (ruleType, setId, documentTypes). The form holds these as react-hook-form
// state, then the create/update path re-nests them via getInputFromData.
function flattenRuleForForm(structured: any | null | undefined) {
  if (!structured) return { ruleType: undefined, setId: undefined, documentTypes: undefined };
  if (structured.investmentsDynamicShare) {
    return {
      ruleType: "InvestmentsDynamicShare",
      setId: structured.investmentsDynamicShare.investmentSet?.setId,
      documentTypes: undefined,
    };
  }
  if (structured.firmsDynamicShare) {
    return {
      ruleType: "FirmsDynamicShare",
      setId: structured.firmsDynamicShare.investmentSet?.setId,
      documentTypes: undefined,
    };
  }
  if (structured.personsDynamicShare) {
    return {
      ruleType: "PersonsDynamicShare",
      setId: structured.personsDynamicShare.investmentSet?.setId,
      documentTypes: undefined,
    };
  }
  if (structured.documentsDynamicShare) {
    return {
      ruleType: "DocumentsDynamicShare",
      setId: structured.documentsDynamicShare.investmentSet?.setId,
      documentTypes: structured.documentsDynamicShare.documentTypes ?? undefined,
    };
  }
  if (structured.everything) {
    return { ruleType: "Everything", setId: undefined, documentTypes: undefined };
  }
  return { ruleType: undefined, setId: undefined, documentTypes: undefined };
}

async function getOne(sdk: Sdk, params: types.GetOneParams) {
  const id = Number(params.id);
  const { groupShareDefinition: data } = await sdk.getOneGroupShareDefinition({
    id,
  });

  if (!data) {
    throw new Error(`GroupShareDefinition with id ${id} not found`);
  }

  const transformedData = {
    ...data,
    ...flattenRuleForForm(data.dynamicDefinitionStructured),
  };

  return { data: transformedData };
}

async function getMany(sdk: Sdk, params: types.GetManyParams) {
  const ids = params.ids.map(Number);
  const { groupShareDefinitionMany: data } =
    await sdk.getManyGroupShareDefinition({
      ids,
    });
  return { data };
}

async function getManyReference(
  sdk: Sdk,
  params: types.GetManyReferenceParams<client.GroupShareDefinitionSortEnum>
) {
  const {
    pagination: { page, perPage },
    sort: { field, order },
    filter,
    target,
    id,
  } = params;

  const {
    groupShareDefinitionList: { items: rawData, total },
  } = await sdk.getListGroupShareDefinition({
    page: {
      offset: (page - 1) * perPage,
      limit: perPage,
    },
    sort: [{ field, order }],
    filter: {
      ...filter,
      [target]: id,
    },
  });

  // Transform structured data for form editing
  const data = rawData.map(item => ({
    ...item,
    ...flattenRuleForForm(item.dynamicDefinitionStructured),
  }));

  return {
    data,
    total,
  };
}

function getInputFromData(
  data: Partial<
    client.GroupShareDefinition & {
      setId?: number;
      documentTypes?: number[];
      ruleType?: string;
    }
  >
): client.GroupShareDefinitionInput {
  let dynamicDefinition = null;

  const investmentSet = data.setId ? { setId: data.setId } : undefined;

  switch (data.ruleType) {
    case "InvestmentsDynamicShare":
      if (!investmentSet)
        throw new Error("InvestmentsDynamicShare requires an investment set");
      dynamicDefinition = {
        description: `Investments share (Set ID: ${data.setId})`,
        displayName: "Investments",
        investmentsDynamicShare: { investmentSet },
      };
      break;
    case "FirmsDynamicShare":
      if (!investmentSet)
        throw new Error("FirmsDynamicShare requires an investment set");
      dynamicDefinition = {
        description: `Firms share (Set ID: ${data.setId})`,
        displayName: "Firms",
        firmsDynamicShare: { investmentSet },
      };
      break;
    case "PersonsDynamicShare":
      if (!investmentSet)
        throw new Error("PersonsDynamicShare requires an investment set");
      dynamicDefinition = {
        description: `Persons share (Set ID: ${data.setId})`,
        displayName: "Persons",
        personsDynamicShare: { investmentSet },
      };
      break;
    case "DocumentsDynamicShare":
      if (!investmentSet)
        throw new Error("DocumentsDynamicShare requires an investment set");
      dynamicDefinition = {
        description: `Documents share (Set ID: ${data.setId})`,
        displayName: "Documents",
        documentsDynamicShare: {
          investmentSet,
          documentTypes: data.documentTypes?.length
            ? data.documentTypes
            : undefined,
        },
      };
      break;
    case "Everything":
      dynamicDefinition = {
        description: "Share all entities from source group",
        displayName: "Everything",
        everything: true,
      };
      break;
  }

  return {
    dynamicDefinition,
    sourceGroupId: data.sourceGroupId!,
    targetGroupId: data.targetGroupId!,
    entityAccessId: data.entityAccessId!,
    isActive: data.isActive ?? true,
  };
}

// After the mutation has committed, refetch to populate relations
// (targetGroup, sourceGroup) and dynamicDefinitionStructured. If the refetch
// itself fails (replication lag, transient network, server returns null), we
// must NOT surface that as a mutation failure — the write already succeeded
// on the server, and reporting failure would prompt the user to retry and
// create a duplicate. Fall back to the merged form data + new id; the row
// will lack relations until the next refresh.
async function refetchOrFallback(
  sdk: Sdk,
  id: number,
  fallbackData: Record<string, unknown>
): Promise<{ data: any }> {
  try {
    return await getOne(sdk, { id });
  } catch (err) {
    captureException(err, {
      tags: { feature: "groupShareDefinition:postMutationRefetch" },
    });
    return { data: { ...fallbackData, id } };
  }
}

async function create(
  sdk: Sdk,
  params: types.CreateParams<client.GroupShareDefinition>
) {
  const input = getInputFromData(params.data);
  const { addGroupShareDefinition } = await sdk.createGroupShareDefinition({
    input,
  });
  return refetchOrFallback(sdk, addGroupShareDefinition.id, params.data);
}

async function update(
  sdk: Sdk,
  params: types.UpdateParams<client.GroupShareDefinition>
) {
  const id = Number(params.id);
  const input = getInputFromData(params.data);
  const { updateGroupShareDefinition } = await sdk.updateGroupShareDefinition({
    id,
    input,
  });
  return refetchOrFallback(sdk, updateGroupShareDefinition.id, params.data);
}

async function deleteOne(sdk: Sdk, params: types.DeleteParams) {
  const id = Number(params.id);
  await sdk.deleteGroupShareDefinition({ id });
  return { data: { id } };
}

export const groupShareDefinition = types.dataProvider({
  getList,
  getOne,
  getMany,
  getManyReference,
  create,
  update,
  delete: deleteOne,
});
